import type { BookkeepingDisplayStage } from './bookkeeping-display-stage'

export interface BookkeepingStepDescriptor {
  title: string
  description: string
  stage: BookkeepingDisplayStage
  isCompleted: boolean
  isCurrent: boolean
}

export interface GetComputedBookkeepingStepsOptions {
  currentStage?: BookkeepingDisplayStage
  ratingEnabled: boolean
}

/**
 * The bookkeeping steps.
 */
const bookkeepingSteps: BookkeepingStepDescriptor[] = [
  {
    title: 'Access required',
    description:
      'Please connect your QuickBooks or Xero account for your Pro to access your books.',
    stage: 'PendingAccess',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Access confirmation',
    description: 'An admin is confirming access to your account.',
    stage: 'AdminConfirmation',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Assessing your books',
    description: 'Your Pro is currently assessing your books.',
    stage: 'AssessingBooks',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Ready to start',
    description: 'Your Pro will start working on your books soon.',
    stage: 'ReadyToStart',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Bookkeeping in progress',
    description: 'Your Pro is currently working on your books.',
    stage: 'InProgress',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Bookkeeping cycle completed',
    description:
      'Your Pro has completed the previous bookkeeping cycle. They will start the next cycle within the first few days of next month.',
    stage: 'CycleComplete',
    isCompleted: false,
    isCurrent: false,
  },
  {
    title: 'Bookkeeping completed',
    description:
      'Your Pro has completed your bookkeeping request. Please let your Pro know if you would like to extend your bookkeeping job.',
    stage: 'BookkeepingCompleted',
    isCompleted: false,
    isCurrent: false,
  },
]

/**
 * Computes the bookkeeping steps.
 */
export function computeBookkeepingSteps(
  options: GetComputedBookkeepingStepsOptions,
) {
  return mapToStepList(bookkeepingSteps, options.currentStage)
}

/**
 * Maps the steps to the computed steps.
 */
function mapToStepList(
  steps: BookkeepingStepDescriptor[],
  currentDisplayStage?: BookkeepingDisplayStage,
) {
  const currentStageIndex = steps.findIndex(
    (step) => step.stage === currentDisplayStage,
  )

  return steps.map((step, index) => ({
    ...step,
    isCompleted: index < currentStageIndex,
    isCurrent: index === currentStageIndex,
  }))
}

/**
 * Gets the current bookkeeping step.
 */
export function getCurrentStep(
  allSteps: BookkeepingStepDescriptor[],
  stage?: BookkeepingDisplayStage,
) {
  return allSteps.find((x) => x.stage === stage)
}
